import { isoDate } from '@mnd-frontend/dates';
import { Sentiment } from '@mnd-frontend/graphql-schema';
import { DANGER_500, GREY_400, SUCCESS_500 } from '@mnd-frontend/ui';
import { useCallback, useMemo, useState } from 'react';
import { InteractionContextProvider } from '../../contexts/InteractionContext';
import { useReportStaticData, useReportTracking, useReportTranslation } from '../../hooks';
import { type HitsModalDetails, HitsModalSentiment, SkeletonBarChart } from '../common';
import { DonutChart, DonutContainer } from './DonutChart';
import { LegendSentimentOverview } from './GraphLegend/Legend';

const SentimentDonut = ({
  positive,
  negative,
  neutral,
}: {
  positive: number;
  negative: number;
  neutral: number;
}) => {
  const { t } = useReportTranslation();
  const { fromDate, toDate } = useReportStaticData();

  const data = useMemo(
    () => [
      {
        id: 'positive',
        filter: Sentiment.Positive,
        label: t('reports_sentiment-positive', { count: 1 }),
        value: positive,
        color: SUCCESS_500,
        index: 0,
      },
      {
        id: 'negative',
        filter: Sentiment.Negative,
        label: t('reports_sentiment-negative', { count: 1 }),
        value: negative,
        color: DANGER_500,
        index: 1,
      },
      {
        id: 'neutral',
        filter: Sentiment.Neutral,
        label: t('reports_sentiment-neutral', { count: 1 }),
        value: neutral,
        color: GREY_400,
        index: 2,
      },
    ],
    [t, positive, negative, neutral],
  );

  const [details, setDetails] = useState<HitsModalDetails | null>(null);
  const { track } = useReportTracking();
  const onArcClick = useCallback(
    (id: string) => {
      const dataPoint = data.find(d => d.id === id);
      if (!dataPoint) return;

      track({
        event: 'Brand Reports: Clicked Sentiment Overview graph',
      });
      setDetails({
        startAt: isoDate(fromDate),
        endAt: isoDate(toDate),
        label: dataPoint.label,
        filter: dataPoint.filter,
      });
    },
    [data, fromDate, toDate, track],
  );
  const onModalClose = useCallback(() => setDetails(null), []);

  return (
    <div data-testid="sentiment-overview-graph">
      <DonutContainer>
        <InteractionContextProvider onClick={onArcClick}>
          <DonutChart data={data} />
          <LegendSentimentOverview data={data} />
        </InteractionContextProvider>
      </DonutContainer>
      {details ? <HitsModalSentiment onClose={onModalClose} details={details} /> : null}
    </div>
  );
};

export const SentimentOverviewGraph = ({
  loading,
  positive,
  negative,
  neutral,
}: {
  loading: boolean;
  positive: number;
  negative: number;
  neutral: number;
}) => {
  return loading ? (
    <SkeletonBarChart />
  ) : (
    <SentimentDonut positive={positive} negative={negative} neutral={neutral} />
  );
};
